import React, { useCallback, useEffect, useState } from 'react';
import { Document } from '../domain/types';

interface Props {
  isDocumentResizing: boolean;
  documentResizingId: string | null;
  setIsDocumentResizing: (isResizing: boolean) => void;
  setDocumentResizingId: (id: string | null) => void;
  movingItem: { type: 'comment' | 'label' | 'widget' | 'document'; id: string } | null;
  showDocuments: boolean;
  documents: Document[];
  onDocumentMove: (id: string, x: number, y: number) => void;
  onDocumentScale: (id: string, scale: number) => void;
  onDocumentDelete: (id: string) => void;
  onDocumentContextMenu: (id: string, x: number, y: number) => void;
  getWorldToCanvas: (x: number, y: number) => { x: number; y: number };
  currentZoom: number;
  colorTheme?: 'sdaia' | 'default';
  theme?: 'light' | 'dark';
}

const DocumentLayer: React.FC<Props> = ({
  isDocumentResizing,
  documentResizingId,
  setIsDocumentResizing,
  setDocumentResizingId,
  movingItem,
  showDocuments,
  documents,
  onDocumentMove,
  onDocumentScale,
  onDocumentDelete,
  onDocumentContextMenu,
  getWorldToCanvas,
  currentZoom,
  colorTheme = 'default',
  theme = 'light'
}) => {
  const [localDocumentScales, setLocalDocumentScales] = useState<Record<string, number>>({});
  const [resizingDocument, setResizingDocument] = useState<string | null>(null);
  // Cache object URLs for local File objects so we don't recreate them every render
  const [objectUrls, setObjectUrls] = useState<Record<string, string>>({});
  const prevUrlsRef = React.useRef<Record<string, string>>({});
  // Track file references to detect when files change
  const prevFilesRef = React.useRef<Record<string, File>>({});

  // Function to get the current zoom scale for documents (same as widgets)
  const getDocumentZoomScale = (doc: Document) => {
    // Check if we have a local scale for this document first (for immediate feedback during resize)
    // Fall back to the document's scale from backend data
    const baseScale = localDocumentScales[doc.id] !== undefined
      ? localDocumentScales[doc.id]
      : (doc.scale || 1);

    // Always maintain consistent visual size regardless of zoom (like widgets)
    // Use direct scaling to maintain pixel size
    return baseScale * currentZoom;
  };

  useEffect(() => {
    const prev = prevUrlsRef.current || {};
    const prevFiles = prevFilesRef.current || {};
    const next: Record<string, string> = {};
    const nextFiles: Record<string, File> = {};

    documents.forEach((d) => {
      if (d.file) {
        // Check if file has changed by comparing file references
        const fileChanged = prevFiles[d.id] !== d.file;
        
        // if we already created a URL for this id and the file hasn't changed, reuse it
        if (prev[d.id] && !fileChanged) {
          next[d.id] = prev[d.id];
          nextFiles[d.id] = d.file as File;
        } else {
          // File changed or is new, revoke old URL if exists and create new one
          if (prev[d.id]) {
            try {
              URL.revokeObjectURL(prev[d.id]);
            } catch (err) {
              void 0;
            }
          }
          try {
            next[d.id] = URL.createObjectURL(d.file as File);
            nextFiles[d.id] = d.file as File;
          } catch (err) {
            void 0;
          }
        }
      }
    });

    // revoke any previous URLs that are no longer used
    Object.keys(prev).forEach((id) => {
      if (!next[id]) {
        try {
          URL.revokeObjectURL(prev[id]);
        } catch (err) {
          void 0;
        }
          }
        });

    prevUrlsRef.current = next;
    prevFilesRef.current = nextFiles;
    setObjectUrls(next);

    return () => {
      Object.values(next).forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (_e) { /* ignore */ }
      });
    };
  }, [documents]);


  // Handle document resize (same pattern as widgets)
  const handleDocumentResize = useCallback((documentId: string) => {
    const doc = documents.find((d) => d.id === documentId);
    if (!doc) return;

    const startX = window.event instanceof MouseEvent ? window.event.clientX : 0;
    const initialScale = localDocumentScales[documentId] ?? doc.scale ?? 1;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate scale based on mouse movement
      const dx = e.clientX - startX;
      const newScale = initialScale + dx * 0.003; // Increased sensitivity for faster resizing (was 0.001)
      const clampedScale = Math.max(0.01, newScale); // Very small minimum to allow tiny sizes

      // Update local state for immediate visual feedback
      setLocalDocumentScales(prev => ({
        ...prev,
        [documentId]: clampedScale,
      }));

      // Update parent component during drag
      onDocumentScale(documentId, clampedScale);
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      setIsDocumentResizing(false);
      setDocumentResizingId(null);
      setResizingDocument(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    setResizingDocument(documentId);
    setIsDocumentResizing(true);
    setDocumentResizingId(documentId);
  }, [documents, localDocumentScales, onDocumentScale, setIsDocumentResizing, setDocumentResizingId]);

  // Handle global click to stop movement
  useEffect(() => {
    const handleGlobalMouseDown = (e: MouseEvent) => {
      // Handle left click only
      if (e.button !== 0) return;

      // Stop movement on any left click (including outside the document)
      if (movingItem?.type === 'document') {
        const mouseEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        window.dispatchEvent(mouseEvent);
      }
    };

    const handleGlobalMouseUp = () => {
      // Ensure movement stops on any mouseup
      if (movingItem?.type === 'document') {
        const mouseEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        window.dispatchEvent(mouseEvent);
      }
    };

    document.addEventListener('mousedown', handleGlobalMouseDown);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mousedown', handleGlobalMouseDown);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [movingItem]);


  if (!showDocuments) return null;

  return (
    <>
      {/* Green dot indicators for moving documents */}
      {documents.map((doc) => {
        const isMovingThis = movingItem?.type === 'document' && movingItem?.id === doc.id;
        if (!isMovingThis) return null;

        const useX = (doc as any).tempX !== undefined ? (doc as any).tempX : doc.x;
        const useY = (doc as any).tempY !== undefined ? (doc as any).tempY : doc.y;
        const screenPos = getWorldToCanvas(useX, useY);

        return (
          <div
            key={`green-dot-${doc.id}`}
            style={{
              position: 'absolute',
              left: screenPos.x - 8,
              top: screenPos.y - 8,
              width: '16px',
              height: '16px',
              backgroundColor: '#22C55E',
              borderRadius: '50%',
              border: '2px solid #16A34A',
              zIndex: 1002, // Above document but below UI elements
              pointerEvents: 'none',
              boxShadow: '0 0 8px rgba(34, 197, 94, 0.6)',
            }}
          />
        );
      })}

      {documents.map((doc) => {
        // Use temporary position if available (during movement)
        const useX = (doc as any).tempX !== undefined ? (doc as any).tempX : doc.x;
        const useY = (doc as any).tempY !== undefined ? (doc as any).tempY : doc.y;

        const screenPos = getWorldToCanvas(useX, useY);

        const isMoving = movingItem?.type === 'document' && movingItem?.id === doc.id;
        const isResizing = documentResizingId === doc.id;

        // Use very high resolution base (4x) for maximum crisp PDF rendering
        const baseWidth = 1200; // 4x resolution for maximum clarity
        const baseHeight = 1696; // A4 ratio (1200 × 1.414)
        
        return (
          <div
            key={doc.id}
            className={`absolute ${isMoving ? '' : 'transition-transform duration-200'}`}
            style={{
              // Use the exact same positioning as widgets
              left: `${screenPos.x}px`,
              top: `${screenPos.y}px`,
              transform: `translate(-50%, -50%) scale(${getDocumentZoomScale(doc) * 0.25})`,
              transformOrigin: 'center',
              // Very high resolution base dimensions for maximum crispness
              width: `${baseWidth}px`,
              height: `${baseHeight}px`,
              background: theme === 'dark' ? '#1B1B1B' : '#FFFFFF',
              border: `1px solid ${theme === 'dark' ? '#2D2D2D' : '#D1D5DB'}`,
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              zIndex: isMoving || isResizing ? 1000 : 9,
              cursor: isMoving ? 'grabbing' : 'grab',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              touchAction: 'none',
              pointerEvents: isMoving ? 'none' : 'auto',
              outline: isResizing ? '2px dashed rgba(255, 255, 255, 0.5)' : 'none',
              // Anti-blur properties (same as widgets)
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transformStyle: 'flat',
              willChange: 'transform',
            }}
            onMouseDown={(e) => {
              if (isResizing) return; // Don't start moving if we're resizing
              e.preventDefault();
              e.stopPropagation();
              
              // Only process left mouse button clicks
              if (e.button === 0) {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickInHeader = e.clientY - rect.top < 40; // Header height
                
                if (clickInHeader) {
                  onDocumentContextMenu(doc.id, e.clientX, e.clientY);
                } else if (isMoving) {
                  // If already moving, stop the movement immediately
                  window.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
                } else {
                  // Initialize movement tracking
                  const startMousePos = { x: e.clientX, y: e.clientY };
                  const startDocPos = { x: doc.x, y: doc.y };
                  const canvasRect = document.querySelector('canvas')?.getBoundingClientRect();
                  if (!canvasRect) return;

                  let rafId: number | null = null;

                  const handleMouseMove = (moveEvent: MouseEvent) => {
                    if (!isMoving) return;

                    // Calculate cursor movement in screen coordinates
                    const dx = moveEvent.clientX - startMousePos.x;
                    const dy = moveEvent.clientY - startMousePos.y;

                    // Convert screen movement to world coordinates
                    // Use the same scaling as widgets for consistency
                    const worldDx = dx / currentZoom;
                    const worldDy = dy / currentZoom;

                    // Calculate new absolute position in world coordinates
                    const newWorldX = startDocPos.x + worldDx;
                    const newWorldY = startDocPos.y + worldDy;

                    // Use RAF for smooth movement
                    if (rafId === null) {
                      rafId = requestAnimationFrame(() => {
                        // Update position using absolute coordinates
                        onDocumentMove(doc.id, newWorldX - doc.x, newWorldY - doc.y);
                        rafId = null;
                      });
                    }
                  };

                  const handleMouseUp = () => {
                    window.removeEventListener('mousemove', handleMouseMove);
                    window.removeEventListener('mouseup', handleMouseUp);
                    if (rafId !== null) {
                      cancelAnimationFrame(rafId);
                      rafId = null;
                    }
                    // Clear any active movement state and ensure final position is saved
                    const finalDx = (window.event instanceof MouseEvent ? window.event.clientX : e.clientX) - startMousePos.x;
                    const finalDy = (window.event instanceof MouseEvent ? window.event.clientY : e.clientY) - startMousePos.y;
                    const finalWorldDx = finalDx / currentZoom;
                    const finalWorldDy = finalDy / currentZoom;
                    const finalX = startDocPos.x + finalWorldDx;
                    const finalY = startDocPos.y + finalWorldDy;
                    onDocumentMove(doc.id, finalX - doc.x, finalY - doc.y);
                  };

                  window.addEventListener('mousemove', handleMouseMove);
                  window.addEventListener('mouseup', handleMouseUp);
                }
              }
            }}
          >
            {/* Document header */}
            <div 
              className="flex justify-between items-center select-none relative"
              style={{
                background: theme === 'dark' ? '#2D2D2D' : '#F3F4F6',
                borderBottom: `4px solid ${theme === 'dark' ? '#404040' : '#E5E7EB'}`, // 4x border
                padding: '32px' // 4x padding (8px * 4)
              }}
            >
              <span 
                className="font-medium truncate flex-1 cursor-move" 
                style={{ 
                  color: theme === 'dark' ? '#FFFFFF' : '#111827',
                  fontSize: '56px' // 4x scaled to appear as 14px when container scaled to 0.25
                }}
                onMouseDown={(e) => {
                  if (isResizing) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {doc.name}
              </span>
              <div className="flex items-center space-x-1 pointer-events-auto">
                <button
                  type="button"
                  className="p-1 rounded hover:bg-black/10 cursor-pointer"
                  style={{ 
                    color: theme === 'dark' ? '#FFFFFF' : '#111827',
                    position: 'relative',
                    zIndex: 1050
                  }}
                >
                  {/* Three dots menu icon */}
                  <svg 
                    fill="currentColor" 
                    viewBox="0 0 16 16"
                    style={{
                      width: '64px', // 4x scaled to appear as 16px when container scaled to 0.25
                      height: '64px'
                    }}
                  >
                    <circle cx="8" cy="2" r="1.5" />
                    <circle cx="8" cy="8" r="1.5" />
                    <circle cx="8" cy="14" r="1.5" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Document content */}
            <div 
              className="h-[calc(100%-2.5rem)] overflow-hidden"
              onMouseDown={(e) => e.stopPropagation()}>
              {/* Enable pointer events only when not moving or resizing and ensure full height */}
              <div
                className={`w-full h-full ${isMoving || resizingDocument === doc.id ? 'pointer-events-none' : 'pointer-events-auto'}`}
                style={{
                  transition: 'none',
                  transform: 'translate3d(0,0,0)',
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale',
                }}
              >
                {(() => {
                  // Use FitH for better rendering and zoom=100 for crisp display
                  const baseFragment = `toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=100`;

                  const embedSrc = doc.fileUrl
                    ? `${doc.fileUrl}#${baseFragment}`
                    : objectUrls[doc.id]
                    ? `${objectUrls[doc.id]}#${baseFragment}`
                    : '';

                  if (!embedSrc) {
                    return (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs text-center px-4">
                        Loading document...
                      </div>
                    );
                  }

                  return (
                    <iframe
                      src={embedSrc}
                      style={{ 
                        display: 'block', 
                        width: '100%', 
                        height: '100%',
                        border: 'none',
                        backgroundColor: 'transparent',
                      }}
                      title={doc.name}
                    />
                  );
                })()}
              </div>
            </div>

            {/* Transparent overlay during resize to capture mouseup */}
            {resizingDocument === doc.id && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1000,
                  cursor: 'nwse-resize',
                  pointerEvents: 'auto',
                }}
              />
            )}

            {/* Resize handle */}
            <div
              data-resize-handle={doc.id}
              style={{
                position: 'absolute',
                top: '0px',
                right: '0px',
                width: '64px', // 4x scaled (16px * 4)
                height: '64px', // 4x scaled (16px * 4)
                cursor: 'ne-resize',
                zIndex: 1001,
                pointerEvents: isMoving ? 'none' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isResizing ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px', // 4x scaled (3px * 4)
                border: `4px solid ${isResizing ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.3)'}`, // 4x border
                transition: 'all 0.2s ease',
                opacity: isResizing ? 1 : 0,
                visibility: isResizing ? 'visible' : 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.visibility = 'visible';
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.opacity = '0';
                  e.currentTarget.style.visibility = 'hidden';
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDocumentResize(doc.id);
              }}
            >
              <svg width="40" height="40" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M1 9L9 1M9 9L9 1M9 1L1 1"
                  stroke="rgba(255, 255, 255, 0.7)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default React.memo(DocumentLayer);